/**
 * api 统一封装方法
 * @description api集合为元数据，可直接在页面使用 this.$api[module][api]某某方法
 * @example api.login.logout(queryOrBodyData, opt)
 *
 * @param {Object} queryOrBodyData? 查询参数 或者 荷载数据
 * @param {Boolean} opt axios配置
 *
 * @return {Object<{ data }>}
 */

import { message as Message } from 'ant-design-vue';
import axios, { domain } from '@/plugins/axios';
import utils from '@/plugins/utils'
import Md5 from 'js-md5';
import qs from 'qs';

import CommonApi from './modules/common.api';
import LoginApi from './modules/login.api';
import AccountApi from './modules/account.api';
import LinanApi from './modules/linan.api';
import orderApi from './modules/order.api';
import sortcategoryApi from './modules/sortcategory.api'

const _domain = process.env.VUE_APP_API_URL;
// 合并所有模块api并导出到外部， 可直接在页面使用 this.$api.某某方法
const APIS = {
    common: CommonApi,
    login: LoginApi,
    account: AccountApi,
    linan: LinanApi,
    order: orderApi,
    sortCategory: sortcategoryApi
}
/**
 * 请求前 中间件
 * @param {ApiMeta} apiMeta API元数据
 * @param {Query/Body} data 请求数据
 */
function foreRequestMiddleWare(apiMeta, data) {
    const { schema, name } = apiMeta;

};

/**
 * 请求失败后 中间件
 * @param {ApiMeta} apiMeta API元数据
 * @param {Error} error 请求错误对象
 * @param {Function} next 下一步
 */
function fallbackMiddleWare(apiMeta, error, next) {
    const { name, method, url } = apiMeta;

    console.error(`[${method}][${domain}${url}]: ${name} 失败，${error.message}`);
    // Message.error(`${name} 失败，${error.message}`);
    next(error);
};

// 将单个模块的 meta 转换为 请求
function Proxyable(target) {
    const target_ = {};
    for (const key in target) {
        if (target.hasOwnProperty(key)) {
            target_[key] = ProxyApi(target, key);
        }
    }
    return target_;
}

// 将 ApiMeta 映射为 http 请求
function ProxyApi(target, key) {
    if (!target[key]) throw new ReferenceError('API ' + key + ' not exist');

    const {
        method,
        url,
        name
    } = target[key];
    if (method.toUpperCase() === 'GET') {
        return (query = {}, opt) => new Promise((resolve, reject) => {
            query = qs.parse(query);
            let md5data = utils.addSignData(opt.params);//此方法是为了添加固定写死的值，省去重复操作。
            md5data = utils.sortByKey(md5data);// 按照键名排序
            md5data["secret"] = md5data.nonce;//这个字段是在排序之后再添加，不进行单独的数据传递，值写死
            let dataStr = utils.doMd5Data(md5data);//根据验签规则进行的数据处理
            md5data["signature"] = Md5(dataStr).toUpperCase();//加密数据全部转大写
            delete md5data["secret"];//这个字段不用单独传，再传值之前要删掉
            opt.params = md5data;
            foreRequestMiddleWare(target[key], query);
            axios.get(url, Object.assign({}, {
                params: query,
                headers: {
                    "appid": opt.params.appid,
                    "timestamp": opt.params.timestamp,
                    "nonce": opt.params.nonce,
                    "signature": opt.params.signature
                }
            }, opt))
                .then(data => resolve(data))
                .catch(err => {
                    fallbackMiddleWare(target[key], err, reject)
                });
        })
    } else if (method.toUpperCase() === 'DELETE') {
        return (body = {}, opt) => new Promise((resolve, reject) => {
            let md5data = utils.addSignData(body);//此方法是为了添加固定写死的值，省去重复操作。
            md5data = utils.sortByKey(md5data);// 按照键名排序
            md5data.secret = md5data.nonce;//这个字段是在排序之后再添加，不进行单独的数据传递，值写死
            let dataStr = utils.doMd5Data(md5data);//根据验签规则进行的数据处理
            md5data.signature = Md5(dataStr).toUpperCase();//加密数据全部转大写
            delete md5data.secret;//这个字段不用单独传，再传值之前要删掉
            let qsBody = qs.stringify(md5data);
            foreRequestMiddleWare(target[key], body);
            axios.delete(url, { data: qsBody }, qsBody, {
                headers: {
                    "appid": body.appid,
                    "timestamp": body.timestamp,
                    "nonce": body.nonce,
                    "signature": body.signature
                }
            }, opt)
                .then(data => resolve(data))
                .catch(err => {
                    fallbackMiddleWare(target[key], err, reject)
                });
        })
    } else if (method.toUpperCase() === 'POST') {
        return (body = {}, opt) => new Promise((resolve, reject) => {
            let md5data = utils.addSignData(body);//此方法是为了添加固定写死的值，省去重复操作。
            md5data = utils.sortByKey(md5data);// 按照键名排序
            md5data.secret = md5data.nonce;//这个字段是在排序之后再添加，不进行单独的数据传递，值写死
            let dataStr = utils.doMd5Data(md5data);//根据验签规则进行的数据处理
            md5data.signature = Md5(dataStr).toUpperCase();//加密数据全部转大写
            delete md5data.secret;//这个字段不用单独传，再传值之前要删掉
            let qsBody = qs.stringify(md5data);
            foreRequestMiddleWare(target[key], body);
            axios.post(url, qsBody, {
                headers: {
                    "appid": md5data.appid,
                    "timestamp": md5data.timestamp,
                    "nonce": md5data.nonce,
                    "signature": md5data.signature
                }
            }, opt)
                .then(data => resolve(data))
                .catch(err => {
                    fallbackMiddleWare(target[key], err, reject)
                });
        })
    } else if (method.toUpperCase() === 'PUT') {
        return (body = {}, opt) => new Promise((resolve, reject) => {
            let md5data = utils.addSignData(body);//此方法是为了添加固定写死的值，省去重复操作。
            md5data = utils.sortByKey(md5data);// 按照键名排序
            md5data.secret = md5data.nonce;//这个字段是在排序之后再添加，不进行单独的数据传递，值写死
            let dataStr = utils.doMd5Data(md5data);//根据验签规则进行的数据处理
            md5data.signature = Md5(dataStr).toUpperCase();//加密数据全部转大写
            delete md5data.secret;//这个字段不用单独传，再传值之前要删掉
            let qsBody = qs.stringify(md5data);

            foreRequestMiddleWare(target[key], body);

            axios.put(url, qsBody, {
                headers: {
                    "appid": body.appid,
                    "timestamp": body.timestamp,
                    "nonce": body.nonce,
                    "signature": body.signature
                }
            }, opt)
                .then(data => resolve(data))
                .catch(err => {
                    fallbackMiddleWare(target[key], err, reject)
                });
        })
    } else if (method.toUpperCase() === 'UPLOAD') {
        return (body = {}, opt) => new Promise((resolve, reject) => {
            body = body;

            foreRequestMiddleWare(target[key], body);

            axios.post(url, body, {
                headers: {
                    "appid": body.appid,
                    "timestamp": body.timestamp,
                    "nonce": body.nonce,
                    "signature": body.signature
                }
            }, opt)
                .then(data => resolve(data))
                .catch(err => {
                    fallbackMiddleWare(target[key], err, reject)
                });
        })
    } else {
        throw new Error('【API】API源数据信息错误，不支持的方法：' + method);
    }
}

const API_ = {};
Object.keys(APIS).forEach(apiName => {
    API_[apiName] = Proxyable(APIS[apiName]);
});

export default API_;



// let config = {
//     baseURL: _domain,
//     timeout: 60 * 1000, // Timeout
//     withCredentials: true, // Check cross-site Access-Control
//     headers: {
//         'Content-Type': 'application/x-www-form-urlencoded',
//         'appid' : 'sortingcenter1.0',
//         'nonce' : 'test_sortingcenter'
//     }
// };

// const apiMod = new ApiModule({
//     baseConfig: config,
//     module: true,
//     apiMetas: {
//         common:CommonApi,
//         user: userApi,
//         login: LoginApi,
//         order:OrderApi,
//         account:AccountApi,
//         sparkonto:SparkontoApi
//     }
// });

// apiMod.registerForeRequestMiddleWare(middleWares.foreRequestMiddleWare);
// apiMod.registerFallbackMiddleWare(middleWares.fallbackMiddleWare);

// const apiInstance = apiMod.getInstance();
// const axios = apiMod.getAxios();
// Vue.prototype.$api = apiInstance;

// export default axios;
